import {Alert} from '@mantine/core'
import {AdvancedMarker, Map} from '@vis.gl/react-google-maps'
import {useMemo} from 'react'

import type {GoogleMapsSource} from './google-maps/google-maps-metrics'
import {getGoogleMapsApiKey} from './google-maps/google-maps-provider'
import {MapErrorBoundary} from './google-maps/map-error-boundary'
import {MapLoadObserver, useMapLoadTracking} from './google-maps/map-load-tracking'
import {CREATE_JOB_MAP_ID, LOCATION_MAP_ZOOM} from './location-map-picker'

type LocationMapPreviewProps = {
    source: GoogleMapsSource
    latitude: number
    longitude: number
    height?: number | string
    radius?: number | string
    /**
     * Thumbnail previews are too small for Google's own map controls to be
     * usable, so they render as a still map. Panning, zooming, and a larger
     * view stay in the location editor.
     */
    interactive?: boolean
}

export function LocationMapPreview({
    source,
    latitude,
    longitude,
    height,
    radius = 8,
    interactive = true,
}: LocationMapPreviewProps) {
    const tracking = useMapLoadTracking(source)
    const hasApiKey = Boolean(getGoogleMapsApiKey()?.trim())
    const position = useMemo(() => ({lat: latitude, lng: longitude}), [latitude, longitude])

    if (!hasApiKey) {
        return null
    }

    return (
        <MapErrorBoundary
            onError={tracking.markRuntimeError}
            fallback={
                <Alert color="orange" title="Map unavailable">
                    The location details are still available.
                </Alert>
            }
        >
            <Map
                reuseMaps
                defaultCenter={position}
                defaultZoom={LOCATION_MAP_ZOOM}
                gestureHandling={interactive ? 'cooperative' : 'none'}
                mapId={CREATE_JOB_MAP_ID}
                mapTypeId="hybrid"
                mapTypeControl={false}
                onTilesLoaded={tracking.markReady}
                zoomControl={interactive}
                fullscreenControl={interactive}
                streetViewControl={interactive}
                cameraControl={interactive}
                keyboardShortcuts={interactive}
                clickableIcons={interactive}
                style={{
                    width: '100%',
                    height: height ?? 200,
                    minHeight: height == null ? 240 : undefined,
                    borderRadius: radius,
                }}
            >
                <MapLoadObserver tracking={tracking} />
                <AdvancedMarker position={position} />
            </Map>
        </MapErrorBoundary>
    )
}
